import { useTranslation } from 'react-i18next';
import { aiPreviewItems } from '@/mocks/aiPreview';
import { useRef } from 'react';
import { useSiteSettings } from '@/hooks/useSiteSettings';

const AI_PREVIEW_FALLBACK_IMAGES: Record<string, string> = {
  glossy: 'https://readdy.ai/api/search-image?query=A%20sleek%20cosmetic%20bottle%20with%20a%20high%20gloss%20lacquered%20surface%20reflecting%20soft%20studio%20light%20standing%20on%20a%20clean%20pure%20white%20background%20with%20gentle%20gradient%20shadow%20and%20refined%20minimal%20composition%20for%20elegant%20premium%20beauty%20packaging%20product%20photography&width=800&height=1066&seq=ai-glossy-01&orientation=portrait',
  frosted: 'https://readdy.ai/api/search-image?query=A%20frosted%20translucent%20cosmetic%20bottle%20with%20a%20soft%20matte%20sandblasted%20glass%20texture%20diffusing%20light%20on%20a%20clean%20pure%20white%20studio%20background%20with%20gentle%20shadow%20for%20minimal%20elegant%20skincare%20packaging%20product%20photography&width=800&height=1066&seq=ai-frosted-01&orientation=portrait',
  metallic: 'https://readdy.ai/api/search-image?query=A%20metallic%20silver%20cosmetic%20bottle%20with%20a%20polished%20reflective%20chrome%20finish%20and%20modern%20minimal%20design%20standing%20on%20a%20clean%20white%20studio%20background%20with%20soft%20highlights%20and%20gentle%20shadow%20for%20luxury%20beauty%20packaging%20product%20photography&width=800&height=1066&seq=ai-metallic-01&orientation=portrait',
  matte: 'https://readdy.ai/api/search-image?query=A%20matte%20finish%20cosmetic%20bottle%20with%20a%20smooth%20soft%20touch%20non%20reflective%20surface%20in%20a%20muted%20neutral%20tone%20on%20a%20clean%20pure%20white%20studio%20background%20with%20diffused%20lighting%20and%20subtle%20shadow%20for%20minimal%20premium%20skincare%20packaging%20photography&width=800&height=1066&seq=ai-matte-01&orientation=portrait',
  color: 'https://readdy.ai/api/search-image?query=A%20cosmetic%20bottle%20with%20a%20smooth%20gradient%20color%20finish%20blending%20warm%20peach%20into%20soft%20rose%20tones%20on%20a%20clean%20white%20studio%20background%20with%20soft%20diffused%20lighting%20and%20gentle%20shadow%20for%20modern%20vibrant%20beauty%20packaging%20product%20photography&width=800&height=1066&seq=ai-color-01&orientation=portrait',
  uv: 'https://readdy.ai/api/search-image?query=A%20cosmetic%20bottle%20with%20a%20glossy%20UV%20coated%20finish%20and%20spot%20varnish%20detailing%20creating%20subtle%20raised%20highlights%20on%20a%20clean%20pure%20white%20studio%20background%20with%20soft%20lighting%20for%20premium%20elegant%20packaging%20product%20photography&width=800&height=1066&seq=ai-uv-01&orientation=portrait',
  embossed: 'https://readdy.ai/api/search-image?query=A%20cosmetic%20glass%20bottle%20with%20elegant%20embossed%20raised%20lettering%20and%20textured%20relief%20on%20its%20surface%20under%20soft%20directional%20lighting%20on%20a%20clean%20white%20studio%20background%20with%20gentle%20shadow%20for%20luxury%20skincare%20packaging%20photography&width=800&height=1066&seq=ai-embossed-01&orientation=portrait',
  pearl: 'https://readdy.ai/api/search-image?query=A%20cosmetic%20bottle%20with%20a%20delicate%20pearl%20shimmer%20finish%20reflecting%20iridescent%20pastel%20highlights%20on%20a%20clean%20pure%20white%20studio%20background%20with%20soft%20diffused%20lighting%20and%20subtle%20shadow%20for%20elegant%20premium%20beauty%20packaging%20product%20photography&width=800&height=1066&seq=ai-pearl-01&orientation=portrait',
};

export default function AiPreviewSection() {
  const { t } = useTranslation('home');
  const { settings } = useSiteSettings();
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  return (
    <section className="w-full bg-white py-12 md:py-16 lg:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Header - centered */}
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block text-xs font-semibold text-warm-500 uppercase tracking-[0.2em] mb-4">
              {t('aiPreview.label') || 'AI Technology'}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-800 max-w-3xl mx-auto leading-tight">
              {t('aiPreview.title') || 'Preview Custom Finishes&nbsp;'}
            </h2>
            <p className="text-sm md:text-base text-ink-500 max-w-xl mx-auto mt-4">
              {t('aiPreview.subtitle') || 'Upload your design and see how different finishes transform your packaging in seconds.'}
            </p>
            <div className="w-16 h-1 bg-warm-500 mx-auto mt-6 rounded-full" />
          </div>

          {/* Horizontal scroll carousel */}
          <div className="relative">
            {/* Left arrow */}
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg border border-ink-100 flex items-center justify-center text-ink-600 hover:text-warm-500 hover:border-warm-300 transition-all"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
            </button>

            {/* Right arrow */}
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg border border-ink-100 flex items-center justify-center text-ink-600 hover:text-warm-500 hover:border-warm-300 transition-all"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
            </button>

            {/* Scroll container */}
            <div
              ref={scrollRef}
              className="flex gap-4 md:gap-5 overflow-x-auto pb-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {aiPreviewItems.map((item) => {
                const imgUrl =
                  settings.homeAiPreviewImages[item.id] || AI_PREVIEW_FALLBACK_IMAGES[item.id];
                return (
                  <div
                    key={item.id}
                    className="group relative flex-shrink-0 w-[260px] md:w-[300px] lg:w-[320px] snap-start"
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-ink-100">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={t(item.titleKey)}
                          className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-ink-200 to-ink-100 flex items-center justify-center">
                          <i className="ri-palette-line text-5xl text-ink-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                        <h3 className="text-white font-bold text-base md:text-lg mb-1">
                          {t(item.titleKey)}
                        </h3>
                        <p className="text-white/70 text-sm leading-relaxed">
                          {t(item.descKey)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 md:mt-10 flex justify-center">
            <a
              href="/packaging"
              className="inline-flex items-center gap-2 px-6 py-3 bg-ink-700 text-white text-sm font-medium hover:bg-ink-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-palette-line" />
              {t('aiPreview.tryNow') || 'Explore Custom Finishes'}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}